/**
 * QA-04 — is this page going away? A reload (or any navigation) cancels the page's requests in flight: Chrome right after
 * `pagehide`, WebKit right after `beforeunload` (measured, evals/reports/qa04-2026-10-14), and iOS Safari fires no
 * `beforeunload` at all. The cancelled request rejects like a network failure, and the page used to say so — "I can't reach
 * the bank right now. Nothing was sent" over a request that WAS sent — and keep that line for its next life. When a request
 * fails, the API client asks `gone()` before it reports anything:
 *
 *  - after `pagehide` (until `pageshow` brings the page back from the back-forward cache): yes, at once;
 *  - after a `beforeunload`: it waits for the page to go (`pagehide`: yes) or to stay (`pageshow`, or `BEFOREUNLOAD_MS`
 *    passing — a navigation that did not happen: no). A request is never held longer than that (DCTA-221 review: the first
 *    version held one for good);
 *  - nothing says so yet: a short grace (`graceMs` — iOS: the reload's own `pagehide` comes within it), then the same
 *    questions; nothing then — no: a genuine failure is reported as it always was, a grace later.
 */
const BEFOREUNLOAD_MS = 10_000;
/** How long a failed request waits for a reload's own `pagehide` when nothing has said the page is going. */
export const GRACE_MS = 400;

export class PageLeaving {
  readonly #now: () => number;
  #hidden = false;
  #before: number | undefined;
  /** Requests waiting to hear whether a `beforeunload` became a navigation. */
  readonly #waiting = new Set<(gone: boolean) => void>();
  /** Requests parked while the page went, waiting for it to come back (`shown`). */
  readonly #parked = new Set<() => void>();

  constructor(w: Pick<Window, "addEventListener">, now: () => number = () => performance.now()) {
    this.#now = now;
    w.addEventListener("beforeunload", () => {
      this.#before = this.#now();
    });
    w.addEventListener("pagehide", () => {
      this.#hidden = true;
      this.#settle(true);
    });
    w.addEventListener("pageshow", () => {
      this.#hidden = false;
      this.#before = undefined;
      this.#settle(false);
      for (const back of [...this.#parked]) back();
      this.#parked.clear();
    });
  }

  /** DCTA-221 review: the next `pageshow` — the page back from the back-forward cache; a page that is gone never sees it. */
  shown(): Promise<void> {
    return new Promise((resolve) => this.#parked.add(resolve));
  }

  get leaving(): boolean {
    return this.#hidden || this.#pending();
  }

  /** A request failed: is the page going away? See the module's header for when it answers. */
  async gone(graceMs = GRACE_MS): Promise<boolean> {
    if (this.#hidden) return true;
    if (!this.#pending()) {
      await new Promise((resolve) => setTimeout(resolve, graceMs));
      if (this.#hidden) return true;
      if (!this.#pending()) return false;
    }
    return this.#navigation();
  }

  /** A `beforeunload` came and its navigation has neither happened nor been given up yet. */
  #pending(): boolean {
    return this.#before !== undefined && this.#now() - this.#before < BEFOREUNLOAD_MS;
  }

  /** After a `beforeunload`: the page goes (`pagehide`), stays (`pageshow`), or the navigation's window passes (stays). */
  #navigation(): Promise<boolean> {
    const left = this.#before === undefined ? 0 : BEFOREUNLOAD_MS - (this.#now() - this.#before);
    return new Promise((resolve) => {
      const done = (gone: boolean): void => {
        clearTimeout(timer);
        this.#waiting.delete(done);
        resolve(gone);
      };
      const timer = setTimeout(() => done(false), Math.max(0, left));
      this.#waiting.add(done);
    });
  }

  #settle(gone: boolean): void {
    for (const done of [...this.#waiting]) done(gone);
  }
}
